export async function fetchWithRetries(fn, retries = 3, delay = 1000) {
  let attempt = 0;
  while (attempt < retries) {
      try {
          return await fn();
      } catch (error) {
          attempt++;
          console.warn(`Retrying... (${attempt}/${retries})`);
          if (attempt === retries) {
              throw error; // Rethrow error if all retries fail
          }
          await new Promise(res => setTimeout(res, delay)); // Wait before retrying
      }
  }
}

export function areaColorSwitch(area) {
  switch (area) {
    case 'food':
      return '#C8E6C9';
    case 'living':
      return '#BCDEFB';
    case 'grant':
      return '#FFE0B2';
    case 'consultation':
      return '#D1C3E9';
    case 'funding':
      return '#FFCDD1';
    default:
      return '#ffffff';
  }
}

export function capitalizeAndCamelCase(str) {
  // Split the string into an array based on the dash
  return str
    .split('-')
    // Transform each word: capitalize the first letter of each
    .map((word) =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    // Join the transformed words back into a single string
    .join('')
    .replace(/\.[^/.]+$/, "")
}

export async function loadIcon(exportName) {
  try {
    const module = await import('lucide');
    const { [exportName]: specificExport } = module;
    return specificExport;
  } catch (error) {
    console.error('Failed to load the module:', error);
    return null;
  }
}

export function slugify(str) {
    return String(str)
      .normalize('NFKD') // split accented characters into their base characters and diacritical marks
      .replace(/[\u0300-\u036f]/g, '') // remove all the accents, which happen to be all in the \u03xx UNICODE block.
      .trim() // trim leading or trailing whitespace
      .toLowerCase() // convert to lowercase
      .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
      .replace(/\s+/g, '-') // replace spaces with hyphens
      .replace(/-+/g, '-'); // remove consecutive hyphens
}

export function toRelativeUrl(absUrl) {
  if (absUrl && absUrl !== '') {
    const relativeURL = new URL(absUrl)
    return relativeURL.pathname
  }
  return ''
}

export function stripTags(string) {
  return string.replace(/<[^>]+>/ig,"")
}

export function reorderObjectByKeys(obj, keys) {
  // Create a new object to hold the sorted properties
  const sortedObj = {};

  // Add properties from obj to sortedObj in the order specified by keys
  keys.forEach(key => {
    if (obj.hasOwnProperty(key)) {
      sortedObj[key] = obj[key];
    }
  });

  // Add any remaining properties that weren't specified in keys
  Object.keys(obj).forEach(key => {
    if (!keys.includes(key)) {
      sortedObj[key] = obj[key];
    }
  });

  return sortedObj;
}

function convertTimeToDecimal(timeString) {
  const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
  return hours + minutes / 60;
}

function parseLocalDate(dateString) {
  const [year, month, day] = dateString.split('-').map(Number);
  // Note: month is 0-indexed in JavaScript Date, so subtract 1
  return new Date(year, month - 1, day);
}


function findNextOpeningTime(now, openingTimes, changedTimes, closedDays, locale, mensaid) {
  const weekdays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

  // Loop through the next 7 days
  for (let i = 0; i <= 7; i++) {
    const checkDate = new Date(now);
    checkDate.setDate(now.getDate() + i);

    // Always check if this day is closed by closedDays.
    const isClosed = closedDays.some(({ singleDate, startDate, endDate }) => {
      if (singleDate) {
        const single = new Date(singleDate);
        return single.toDateString() === checkDate.toDateString();
      }
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);  // Adjust end to cover the entire day
        return checkDate >= start && checkDate <= end;
      }
      return false;
    });
    if (isClosed) {
      // Skip this day because closedDays override any schedule.
      continue;
    }

    const dayOfWeek = weekdays[checkDate.getDay()];

    // First check for a changedTimes override for the date.
    let applicableTime = null;
    if (changedTimes && changedTimes.length > 0) {
      applicableTime = changedTimes.find(({ startDate, endDate }) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);  // Adjust end time here as well
        return checkDate >= start && checkDate <= end;
      });
    }
    // Fall back to the regular openingTimes if no changedTimes entry applies.
    if (!applicableTime) {
      applicableTime = openingTimes.find(({ weekday }) => weekday === dayOfWeek);
    }

    if (applicableTime) {
      const openingTimeDecimal = convertTimeToDecimal(applicableTime.openingTime);
      const closingTimeDecimal = convertTimeToDecimal(applicableTime.closingTime);
      const currentTimeDecimal = now.getHours() + now.getMinutes() / 60;
      const isToday = i === 0;
      const isOpenNow = isToday && currentTimeDecimal >= openingTimeDecimal && currentTimeDecimal <= closingTimeDecimal;

      if (isOpenNow) {
        if (locale === 'en') {
          return `<strong>Open</strong> | Closing at ${applicableTime.closingTime.slice(0, -3)}`;
        } else {
          return `<strong>Geöffnet</strong> | Schließt um ${applicableTime.closingTime.slice(0, -3)}`;
        }
      } else if (isToday && currentTimeDecimal < openingTimeDecimal) {
        if (locale === 'en') {
          return `<strong>Closed</strong> | Opens today at ${applicableTime.openingTime.slice(0, -3)}`;
        } else {
          return `<strong>Aktuell geschlossen</strong> | Öffnet heute um ${applicableTime.openingTime.slice(0, -3)}`;
        }
      } else if (i > 0) {
        if (locale === 'en') {
          return `<strong>Closed</strong> | Opens ${checkDate.toLocaleDateString('de-DE')} at ${applicableTime.openingTime.slice(0, -3)}`;
        } else {
          return `<strong>Aktuell geschlossen</strong> | Öffnet am ${checkDate.toLocaleDateString('de-DE')} um ${applicableTime.openingTime.slice(0, -3)}`;
        }
      }
    }
  }
  // If no open schedule is found in the next week, return a closed message.
  return locale === 'en' ? `<strong>Closed</strong>` : `<strong>Aktuell geschlossen</strong>`;
}

export function isOpenNow(openingTimes, changedTimes, closedDays, locale, mensaid) {
  const weekdays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  
  const now = new Date();
  
  // Always check if today is closed regardless of any schedule.
  const isClosedDay = closedDays.some(day => {
    if (day.singleDate) {
      const singleDate = new Date(day.singleDate);
      return singleDate.toDateString() === now.toDateString();
    }
    if (day.startDate && day.endDate) {
      const startDate = new Date(day.startDate);
      const endDate = new Date(day.endDate);
      endDate.setHours(23, 59, 59, 999);  // Adjust end time here as well
      return now >= startDate && now <= endDate;
    }
    return false;
  });
  
  if (isClosedDay) {
    return { isOpen: false, openingText: findNextOpeningTime(now, openingTimes, changedTimes, closedDays, locale, mensaid) };
  }
  
  // First check for a changedTimes override for today.
  let applicableTime = null;
  if (changedTimes && changedTimes.length > 0) {
    applicableTime = changedTimes.find(({ startDate, endDate }) => {
      const start = parseLocalDate(startDate);
      const end = parseLocalDate(endDate);
      // Adjust the end date to cover the full day
      end.setHours(23, 59, 59, 999);
      return now >= start && now <= end;
    });
  }

  // If no changedTimes entry applies, fall back to today's regular schedule.
  if (!applicableTime) {
    const today = weekdays[now.getDay()];
    applicableTime = openingTimes.find(time => time.weekday === today);
  }
  
  if (!applicableTime) {
    return { isOpen: false, openingText: 'Closed' };
  }
  
  const openingTimeDecimal = convertTimeToDecimal(applicableTime.openingTime);
  const closingTimeDecimal = convertTimeToDecimal(applicableTime.closingTime);
  const currentTime = now.getHours() + now.getMinutes() / 60;
  
  if (currentTime >= openingTimeDecimal && currentTime <= closingTimeDecimal) {
    return { isOpen: true, openingText: findNextOpeningTime(now, openingTimes, changedTimes, closedDays, locale, mensaid) };
  }
  
  return { isOpen: false, openingText: findNextOpeningTime(now, openingTimes, changedTimes, closedDays, locale, mensaid) };
}


export function formatOpeningTimes(openingTimes, locale) {
  let dayMappings
  // Mapping English weekdays to German
  if (locale === 'en') {
    dayMappings = {
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday"
    };
  } else {
    dayMappings = {
      monday: "Montag",
      tuesday: "Dienstag",
      wednesday: "Mittwoch",
      thursday: "Donnerstag",
      friday: "Freitag",
      saturday: "Samstag",
      sunday: "Sonntag"
    };
  }

  // Group by consecutive days with the same opening and closing times
  const groups = [];
  openingTimes.forEach((time, i) => {
      const germanDay = dayMappings[time.weekday.toLowerCase()]; // Convert to German and capitalize
      if (i > 0 
          && time.openingTime === openingTimes[i-1].openingTime 
          && time.closingTime === openingTimes[i-1].closingTime
          && groups[groups.length - 1].days.includes(dayMappings[openingTimes[i-1].weekday.toLowerCase()])) {
          groups[groups.length - 1].days.push(germanDay);
      } else {
          groups.push({ days: [germanDay], openingTime: time.openingTime, closingTime: time.closingTime });
      }
  });

  // Convert groups to strings
  const groupStrings = groups.map(group => {
      const daysRange = group.days.length > 1
          ? `${group.days[0]} - ${group.days[group.days.length - 1]}`
          : group.days[0];
      return `${daysRange} ${group.openingTime.slice(0, -3)} bis ${group.closingTime.slice(0, -3)}`;
  });

  // Join all groups with a separator
  return groupStrings;
}

export function flatten(array) {
  return array.reduce(function(memo, el) {
    var items = Array.isArray(el) ? flatten(el) : [el];
    return memo.concat(items);
  }, []);
}

export function groupBy(arr, property) {
  return arr.reduce((acc, cur) => {
    acc[cur[property]] = [...acc[cur[property]] || [], cur];
    return acc;
  }, {});
}

export function formatDate(dateString) {
  const [year, month, day] = dateString.split('-'); // Split the input date
  return `${day}.${month}.${year}`; // Rearrange the parts
}

